import { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import PageHeader from '@/components/Layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SearchableSelect from '@/components/SearchableSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, MagnifyingGlass, Money, Buildings, MapPin, IdentificationBadge,
  Clock, CalendarBlank, Gear, Check, X, Plus, Trash, Upload, FileText,
  MapPinLine, Warning, ArrowsClockwise, Receipt, Laptop, CurrencyDollar,
  ArrowLeft, DownloadSimple, CaretDown, CaretRight, PencilSimple, UserMinus, Eye,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';

const fmt = (v) => formatCurrency(v || 0);
const fmtK = (v) => {
  if (!v) return '0';
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
  return v.toLocaleString();
};

// ─── Employee Detail Modal ──────────────────────────────────────────────────
function EmployeeDetail({ employeeNumber, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profile');
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [docCategories, setDocCategories] = useState([]);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadType, setUploadType] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [taxData, setTaxData] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxForm, setTaxForm] = useState({});
  const [empAssets, setEmpAssets] = useState([]);
  const [form16List, setForm16List] = useState([]);
  const [form16Fy, setForm16Fy] = useState('2024-25');
  const [form16File, setForm16File] = useState(null);
  const [form16Uploading, setForm16Uploading] = useState(false);

  useEffect(() => {
    if (!employeeNumber) return;
    setLoading(true);
    Promise.all([
      hrAPI.getEmployee(employeeNumber),
      hrAPI.getLeaveBalances(employeeNumber),
      hrAPI.getConfig(),
      hrAPI.getAssets({ employee_number: employeeNumber }),
    ]).then(([empRes, leaveRes, configRes, assetRes]) => {
      setData(empRes.data);
      setLeaveBalances(leaveRes.data.balances || []);
      setDocCategories(configRes.data.document_categories || []);
      setEmpAssets(assetRes.data.assets || []);
    }).catch(() => toast.error('Failed to load employee'))
      .finally(() => setLoading(false));
  }, [employeeNumber]);

  const loadTax = useCallback(async () => {
    if (!employeeNumber) return;
    setTaxLoading(true);
    try {
      const res = await hrAPI.getTaxDeclaration(employeeNumber);
      setTaxData(res.data);
      if (res.data.declaration) setTaxForm(res.data.declaration);
      else setTaxForm({ employee_number: employeeNumber, fiscal_year: '2025-26', regime: 'new', section_80c: 0, section_80d: 0, section_80g: 0, hra_exemption: 0, lta_exemption: 0, nps_80ccd: 0, home_loan_interest: 0, other_deductions: 0, other_income: 0 });
    } catch { toast.error('Failed to load tax data'); }
    finally { setTaxLoading(false); }
  }, [employeeNumber]);

  useEffect(() => { if (tab === 'tax') loadTax(); }, [tab, loadTax]);

  const loadForm16 = useCallback(async () => {
    if (!employeeNumber) return;
    try {
      const res = await hrAPI.getForm16({ employee_number: employeeNumber });
      setForm16List(res.data.documents || []);
    } catch { /* ignore */ }
  }, [employeeNumber]);

  useEffect(() => { if (tab === 'tax') loadForm16(); }, [tab, loadForm16]);

  const handleUploadForm16 = async () => {
    if (!form16File || !form16Fy) { toast.error('Select FY and a PDF file'); return; }
    setForm16Uploading(true);
    try {
      await hrAPI.uploadForm16(employeeNumber, form16Fy, form16File);
      toast.success('Form 16 uploaded');
      setForm16File(null);
      loadForm16();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setForm16Uploading(false); }
  };

  const handleDeleteForm16 = async (id) => {
    try { await hrAPI.deleteForm16(id); toast.success('Deleted'); loadForm16(); }
    catch { toast.error('Failed'); }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadType) return;
    try {
      await hrAPI.uploadDocument(employeeNumber, uploadType, uploadFile);
      toast.success('Document uploaded');
      setUploadDialog(false);
      setUploadFile(null);
      const res = await hrAPI.getEmployee(employeeNumber);
      setData(res.data);
    } catch { toast.error('Upload failed'); }
  };

  const handleSaveTax = async () => {
    try {
      await hrAPI.saveTaxDeclaration({ ...taxForm, employee_number: employeeNumber });
      toast.success('Tax declaration saved');
      loadTax();
    } catch { toast.error('Failed to save tax declaration'); }
  };

  const handleDownloadSlip = async (month) => {
    try {
      await hrAPI.downloadSalarySlip(employeeNumber, month);
      toast.success('Salary slip downloaded');
    } catch { toast.error('Failed to download salary slip'); }
  };

  if (!employeeNumber) return null;
  const emp = data?.employee || {};
  const payroll = data?.payroll_history || [];
  const ytd = data?.ytd_summary || {};
  const docs = data?.documents || [];

  return (
    <Dialog open={!!employeeNumber} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="employee-detail-modal">
        <DialogHeader>
          <DialogTitle>{emp.display_name || 'Employee'}</DialogTitle>
          <DialogDescription>{emp.employee_number} — {emp.job_title} — {emp.department}</DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-8 text-center text-[#64748B]">Loading...</div> : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4 flex-wrap">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="salary">Salary</TabsTrigger>
              <TabsTrigger value="leave">Leave</TabsTrigger>
              <TabsTrigger value="tax" data-testid="emp-tab-tax">Tax</TabsTrigger>
              <TabsTrigger value="assets" data-testid="emp-tab-assets">Assets ({empAssets.length})</TabsTrigger>
              <TabsTrigger value="documents">Docs ({docs.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[['Employee #', emp.employee_number], ['Department', emp.department], ['Location', emp.location],
                  ['Status', emp.employment_status], ['Date Joined', emp.date_joined], ['Reporting To', emp.reporting_to],
                  ['Band', emp.band], ['Email', emp.work_email], ['Mobile', emp.mobile_phone],
                  ['Gender', emp.gender], ['DOB', emp.date_of_birth], ['PAN', emp.pan_number],
                  ['PF #', emp.pf_number], ['UAN', emp.uan_number], ['Cost Center', emp.cost_center],
                ].map(([label, value]) => (
                  <div key={label} className="bg-[#F8FAFC] rounded-lg p-3">
                    <p className="text-xs text-[#64748B]">{label}</p>
                    <p className="font-medium text-sm">{value || '—'}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="salary">
              <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Month</th>
                      <th className="px-3 py-2 text-right">Basic</th>
                      <th className="px-3 py-2 text-right">HRA</th>
                      <th className="px-3 py-2 text-right">Gross</th>
                      <th className="px-3 py-2 text-right">PF</th>
                      <th className="px-3 py-2 text-right">Tax</th>
                      <th className="px-3 py-2 text-right">Net Pay</th>
                      <th className="px-3 py-2 text-center">Slip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payroll.map((p, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0]">
                        <td className="px-3 py-2 font-medium">{p.payroll_month}</td>
                        <td className="px-3 py-2 text-right">{fmt(p.basic)}</td>
                        <td className="px-3 py-2 text-right">{fmt(p.hra)}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmt(p.gross)}</td>
                        <td className="px-3 py-2 text-right text-amber-600">{fmt(p.pf_employee)}</td>
                        <td className="px-3 py-2 text-right text-red-600">{fmt(p.income_tax)}</td>
                        <td className="px-3 py-2 text-right font-bold">{fmt(p.total_net_pay)}</td>
                        <td className="px-3 py-2 text-center">
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadSlip(p.payroll_month)} className="h-7 w-7 p-0" data-testid={`download-slip-${i}`}>
                            <DownloadSimple size={14} className="text-[#0F4C81]" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ytd && ytd.gross_ytd > 0 && (
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="bg-[#0F4C81] text-white rounded-lg p-3"><p className="text-xs opacity-70">YTD Gross</p><p className="text-xl font-bold">{fmt(ytd.gross_ytd)}</p></div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3"><p className="text-xs text-amber-600">Deductions</p><p className="text-xl font-bold text-amber-700">{fmt(ytd.total_deductions_ytd)}</p></div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3"><p className="text-xs text-green-600">Net YTD</p><p className="text-xl font-bold text-green-700">{fmt(ytd.net_pay_ytd)}</p></div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="leave">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {leaveBalances.map(lb => (
                  <div key={lb.leave_type_code} className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid={`leave-${lb.leave_type_code}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm">{lb.leave_type_name}</span>
                      <span className="text-xs px-2 py-0.5 bg-[#F8FAFC] rounded">{lb.leave_type_code}</span>
                    </div>
                    <div className="text-2xl font-bold text-[#0F4C81]">{lb.available}</div>
                    <p className="text-xs text-[#64748B] mt-1">Used: {lb.used} | Pending: {lb.pending} | Quota: {lb.annual_quota}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="tax">
              {taxLoading ? <div className="text-center py-8 text-[#64748B]">Loading tax data...</div> : taxData && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[#F8FAFC] rounded-lg p-3"><p className="text-xs text-[#64748B]">Gross Annual (YTD)</p><p className="text-xl font-bold">{fmt(taxData.computation?.gross_annual)}</p></div>
                    <div className="bg-[#F8FAFC] rounded-lg p-3"><p className="text-xs text-[#64748B]">Total Deductions</p><p className="text-xl font-bold text-amber-600">{fmt(taxData.computation?.total_deductions)}</p></div>
                    <div className="bg-[#F8FAFC] rounded-lg p-3"><p className="text-xs text-[#64748B]">Tax Paid YTD</p><p className="text-xl font-bold text-red-600">{fmt(taxData.computation?.tax_paid_ytd)}</p></div>
                  </div>
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                    <h4 className="font-semibold mb-3 text-sm">Tax Declaration — FY {taxForm.fiscal_year || '2025-26'}</h4>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs text-[#64748B]">Tax Regime</label>
                        <Select value={taxForm.regime || 'new'} onValueChange={(v) => setTaxForm(f => ({...f, regime: v}))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New Regime</SelectItem>
                            <SelectItem value="old">Old Regime</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {taxForm.regime === 'old' && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[['section_80c', '80C (PF, PPF, ELSS, etc.)', 150000], ['section_80d', '80D (Health Insurance)', 100000],
                          ['hra_exemption', 'HRA Exemption', 0], ['lta_exemption', 'LTA Exemption', 0],
                          ['nps_80ccd', '80CCD NPS', 50000], ['section_80g', '80G (Donations)', 0],
                          ['home_loan_interest', 'Home Loan Interest (24b)', 200000], ['other_deductions', 'Other Deductions', 0],
                        ].map(([key, label, max]) => (
                          <div key={key}>
                            <label className="text-xs text-[#64748B]">{label}</label>
                            <Input type="number" value={taxForm[key] || 0} onChange={(e) => setTaxForm(f => ({...f, [key]: parseFloat(e.target.value) || 0}))} />
                          </div>
                        ))}
                      </div>
                    )}
                    {taxForm.regime === 'new' && (
                      <p className="text-sm text-[#64748B] py-4">New tax regime: Standard deduction of Rs 75,000 applicable. No other deductions allowed.</p>
                    )}
                    <div className="flex justify-end mt-4">
                      <Button onClick={handleSaveTax} data-testid="save-tax-btn">Save Declaration</Button>
                    </div>
                  </div>

                  {/* Form 16 Management */}
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="hr-form16-card">
                    <h4 className="font-semibold mb-3 text-sm">Form 16</h4>
                    <div className="flex flex-wrap items-end gap-3 mb-3">
                      <div>
                        <label className="text-xs text-[#64748B] block mb-1">Financial Year</label>
                        <Select value={form16Fy} onValueChange={setForm16Fy}>
                          <SelectTrigger className="w-36" data-testid="form16-fy-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['2022-23', '2023-24', '2024-25', '2025-26'].map(fy => <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-[#64748B] block mb-1">PDF File</label>
                        <Input type="file" accept=".pdf" onChange={(e) => setForm16File(e.target.files[0] || null)} className="text-sm w-56" data-testid="form16-file-input" />
                      </div>
                      <Button onClick={handleUploadForm16} disabled={!form16File || form16Uploading} data-testid="form16-upload-btn">{form16Uploading ? 'Uploading...' : 'Upload'}</Button>
                    </div>
                    {form16List.length === 0 ? (
                      <p className="text-xs text-[#64748B]">No Form 16 uploaded for this employee.</p>
                    ) : (
                      <div className="space-y-1">
                        {form16List.map(f => (
                          <div key={f.id} className="flex items-center justify-between bg-[#F8FAFC] rounded px-3 py-2 text-sm" data-testid={`hr-form16-${f.fiscal_year}`}>
                            <span>FY {f.fiscal_year} — <span className="text-[#64748B]">{f.filename}</span></span>
                            <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => handleDeleteForm16(f.id)}><X size={14} /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="assets">
              {empAssets.length > 0 ? (
                <div className="space-y-2">
                  {empAssets.map(a => (
                    <div key={a.id} className="flex items-center justify-between bg-[#F8FAFC] rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <Laptop size={20} className="text-[#0F4C81]" />
                        <div>
                          <p className="font-medium text-sm">{a.asset_name}</p>
                          <p className="text-xs text-[#64748B]">{a.asset_type} | {a.make_model || 'N/A'} | {a.serial_number || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${a.status === 'issued' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{a.status}</span>
                        {a.issued_date && <span className="text-xs text-[#64748B]">Since {a.issued_date}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-center py-8 text-[#64748B]">No assets allocated</p>}
            </TabsContent>

            <TabsContent value="documents">
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={() => setUploadDialog(true)} data-testid="upload-doc-btn">
                  <Upload size={14} className="mr-2" /> Upload Document
                </Button>
              </div>
              {docs.length > 0 ? (
                <div className="space-y-2">
                  {docs.map((d, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#F8FAFC] rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-[#0F4C81]" />
                        <div>
                          <p className="font-medium text-sm">{d.filename}</p>
                          <p className="text-xs text-[#64748B]">{d.doc_type || d.document_type || 'Document'} — {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ''}{d.source === 'employee_portal' ? ' (Uploaded by employee)' : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {d.file_size > 0 && <span className="text-xs text-[#64748B]">{(d.file_size / 1024).toFixed(0)} KB</span>}
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                          const docId = d.id || d._id || '';
                          const url = `${process.env.REACT_APP_BACKEND_URL}/api/hr/documents/${employeeNumber}/${docId}/download`;
                          window.open(url, '_blank');
                        }} data-testid={`view-doc-${i}`}>
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-center py-8 text-[#64748B]">No documents uploaded yet</p>}

              <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <Select value={uploadType} onValueChange={setUploadType}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {docCategories.filter(c => c.is_active).map(c => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input type="file" onChange={(e) => setUploadFile(e.target.files[0])} />
                  </div>
                  <DialogFooter>
                    <Button onClick={handleUpload} disabled={!uploadFile || !uploadType}>Upload</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Leave Management Tab ───────────────────────────────────────────────────
function LeaveManagement() {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [applyDialog, setApplyDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_number: '', leave_type_code: '', start_date: '', end_date: '', reason: '', half_day: false });
  const [editForm, setEditForm] = useState({});
  const [compoffRequests, setCompoffRequests] = useState([]);
  const [compoffStatus, setCompoffStatus] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const [reqRes, configRes, empRes, coRes] = await Promise.all([
        hrAPI.getLeaveRequests(params),
        hrAPI.getConfig(),
        hrAPI.getEmployees({ page_size: 200 }),
        hrAPI.getCompoffRequests({ status: compoffStatus, page_size: 50 }).catch(() => ({ data: { requests: [] } })),
      ]);
      setRequests(reqRes.data.requests || []);
      setTotal(reqRes.data.total || 0);
      setLeaveTypes(configRes.data.leave_types || []);
      setEmployees(empRes.data.employees || []);
      setCompoffRequests(coRes.data.requests || []);
    } catch { toast.error('Failed to load leave data'); }
    finally { setLoading(false); }
  }, [page, statusFilter, compoffStatus]);

  useEffect(() => { load(); }, [load]);

  const handleApply = async () => {
    try {
      await hrAPI.applyLeave(form);
      toast.success('Leave applied');
      setApplyDialog(false);
      setForm({ employee_number: '', leave_type_code: '', start_date: '', end_date: '', reason: '', half_day: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply leave'); }
  };

  const handleAction = async (id, action) => {
    try {
      if (action === 'approve') await hrAPI.approveLeave(id);
      else await hrAPI.rejectLeave(id);
      toast.success(`Leave ${action}d`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
  };

  const handleCompoffAction = async (id, action) => {
    try {
      if (action === 'approve') {
        const res = await hrAPI.approveCompoff(id);
        toast.success(`Comp-off approved — ${res.data.credit_days || 0} day(s) credited`);
      } else {
        await hrAPI.rejectCompoff(id);
        toast.success('Comp-off rejected');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
  };

  const openEdit = (r) => {
    setEditForm({
      leave_type_code: r.leave_type_code,
      start_date: r.start_date,
      end_date: r.end_date,
      reason: r.reason || '',
      half_day: r.half_day || false,
    });
    setEditDialog(r);
  };

  const handleEditSave = async () => {
    try {
      await hrAPI.editLeave(editDialog.id, editForm);
      toast.success('Leave record updated');
      setEditDialog(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to edit'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setApplyDialog(true)} data-testid="apply-leave-btn"><Plus size={14} className="mr-2" /> Apply Leave</Button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Employee</th><th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-left">From</th><th className="px-4 py-3 text-left">To</th><th className="px-4 py-3 text-right">Days</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Reason</th><th className="px-4 py-3 text-center">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">Loading...</td></tr> :
              requests.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">No leave requests</td></tr> :
                requests.map(r => (
                  <tr key={r.id} className="border-t border-[#E2E8F0]">
                    <td className="px-4 py-3 font-medium">{r.employee_name || r.employee_number}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{r.leave_type_code}</span></td>
                    <td className="px-4 py-3">{r.start_date}</td><td className="px-4 py-3">{r.end_date}</td>
                    <td className="px-4 py-3 text-right font-medium">{r.days}{r.lop_days > 0 && <span className="block text-[10px] text-amber-600 font-medium">LOP {r.lop_days}d</span>}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>{r.status === 'pending' && r.approval_stage === 'manager' && <span className="block text-[10px] text-[#64748B] mt-0.5">Awaiting manager</span>}{r.manager_approved_by && <span className="block text-[10px] text-green-600 mt-0.5">Mgr: {r.manager_approved_by}</span>}</td>
                    <td className="px-4 py-3 max-w-[150px] truncate text-[#64748B] text-xs">{r.reason || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex gap-1 justify-center">
                        {r.status === 'pending' && r.approval_stage === 'manager' && (
                          <span className="text-[11px] text-[#94A3B8] italic self-center">Pending manager</span>
                        )}
                        {r.status === 'pending' && r.approval_stage !== 'manager' && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => handleAction(r.id, 'approve')} className="text-green-600 h-7 px-2" data-testid={`approve-leave-${r.id}`}><Check size={14} /></Button>
                            <Button variant="outline" size="sm" onClick={() => handleAction(r.id, 'reject')} className="text-red-600 h-7 px-2" data-testid={`reject-leave-${r.id}`}><X size={14} /></Button>
                          </>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openEdit(r)} className="text-blue-600 h-7 px-2" data-testid={`edit-leave-${r.id}`}><PencilSimple size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      {/* Comp-Off Approvals */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-[#0F4C81]" data-testid="compoff-approvals-heading">Comp-Off Approvals</h3>
            <p className="text-xs text-[#64748B]">Approve earned comp-off requests. Credits CO leave balance automatically.</p>
          </div>
          <Select value={compoffStatus} onValueChange={setCompoffStatus}>
            <SelectTrigger className="w-36" data-testid="compoff-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC]"><tr>
              <th className="px-4 py-3 text-left">Employee</th>
              <th className="px-4 py-3 text-left">Worked Date</th>
              <th className="px-4 py-3 text-right">Hours</th>
              <th className="px-4 py-3 text-left">Reason</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Credit Days</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr></thead>
            <tbody>
              {compoffRequests.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No comp-off requests</td></tr>
              ) : compoffRequests.map(r => (
                <tr key={r.id} className="border-t border-[#E2E8F0]" data-testid={`compoff-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.employee_name || r.employee_number}</td>
                  <td className="px-4 py-3">{r.worked_date}</td>
                  <td className="px-4 py-3 text-right font-medium">{r.hours_worked}h</td>
                  <td className="px-4 py-3 max-w-[200px] truncate text-[#64748B] text-xs">{r.reason || '—'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-right">{r.credit_days ? `${r.credit_days}d` : '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {r.status === 'pending' ? (
                      <div className="flex gap-1 justify-center">
                        <Button variant="outline" size="sm" onClick={() => handleCompoffAction(r.id, 'approve')} className="text-green-600 h-7 px-2" data-testid={`approve-compoff-${r.id}`}><Check size={14} /></Button>
                        <Button variant="outline" size="sm" onClick={() => handleCompoffAction(r.id, 'reject')} className="text-red-600 h-7 px-2" data-testid={`reject-compoff-${r.id}`}><X size={14} /></Button>
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={applyDialog} onOpenChange={setApplyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Apply Leave</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <SearchableSelect value={form.employee_number} onChange={(v) => setForm(f => ({...f, employee_number: v}))} placeholder="Select employee" testid="" buttonClassName="w-full"
                  options={[...employees.map(e => ({ value: e.employee_number, label: `${e.display_name} (${e.employee_number})` }))]} />
            <Select value={form.leave_type_code} onValueChange={(v) => setForm(f => ({...f, leave_type_code: v}))}>
              <SelectTrigger><SelectValue placeholder="Leave type" /></SelectTrigger>
              <SelectContent>{leaveTypes.filter(l => l.is_active).map(l => <SelectItem key={l.code} value={l.code}>{l.name} ({l.code})</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#64748B]">From</label><Input type="date" value={form.start_date} onChange={(e) => setForm(f => ({...f, start_date: e.target.value}))} /></div>
              <div><label className="text-xs text-[#64748B]">To</label><Input type="date" value={form.end_date} onChange={(e) => setForm(f => ({...f, end_date: e.target.value}))} /></div>
            </div>
            <Input placeholder="Reason" value={form.reason} onChange={(e) => setForm(f => ({...f, reason: e.target.value}))} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.half_day} onChange={(e) => setForm(f => ({...f, half_day: e.target.checked}))} /> Half day</label>
          </div>
          <DialogFooter><Button onClick={handleApply} disabled={!form.employee_number || !form.leave_type_code || !form.start_date || !form.end_date}>Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Leave Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Leave Record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">Editing leave for <strong>{editDialog?.employee_name || editDialog?.employee_number}</strong></p>
            <Select value={editForm.leave_type_code || ''} onValueChange={(v) => setEditForm(f => ({...f, leave_type_code: v}))}>
              <SelectTrigger data-testid="edit-leave-type"><SelectValue placeholder="Leave type" /></SelectTrigger>
              <SelectContent>{leaveTypes.filter(l => l.is_active).map(l => <SelectItem key={l.code} value={l.code}>{l.name} ({l.code})</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#64748B]">From</label><Input type="date" value={editForm.start_date || ''} onChange={(e) => setEditForm(f => ({...f, start_date: e.target.value}))} data-testid="edit-leave-from" /></div>
              <div><label className="text-xs text-[#64748B]">To</label><Input type="date" value={editForm.end_date || ''} onChange={(e) => setEditForm(f => ({...f, end_date: e.target.value}))} data-testid="edit-leave-to" /></div>
            </div>
            <Input placeholder="Reason" value={editForm.reason || ''} onChange={(e) => setEditForm(f => ({...f, reason: e.target.value}))} data-testid="edit-leave-reason" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editForm.half_day || false} onChange={(e) => setEditForm(f => ({...f, half_day: e.target.checked}))} /> Half day</label>
          </div>
          <DialogFooter><Button onClick={handleEditSave} data-testid="save-leave-edit-btn">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Attendance Tab ─────────────────────────────────────────────────────────
function AttendanceTab() {
  const [view, setView] = useState('today');
  const [today, setToday] = useState({ records: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [clinics, setClinics] = useState([]);
  const [clinicFilter, setClinicFilter] = useState('all');
  // Report state
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const now = new Date();
  const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const todayStr = now.toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(todayStr);
  // Late report state
  const [lateData, setLateData] = useState(null);
  const [lateLoading, setLateLoading] = useState(false);
  const [lateMonth, setLateMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  // No punch-in state
  const [noPunchData, setNoPunchData] = useState(null);
  const [noPunchLoading, setNoPunchLoading] = useState(false);

  useEffect(() => {
    Promise.all([hrAPI.getTodayAttendance({}), hrAPI.getClinicLocations()])
      .then(([attRes, clinicRes]) => { setToday(attRes.data); setClinics(clinicRes.data.clinic_locations || []); })
      .catch(() => toast.error('Failed to load attendance'))
      .finally(() => setLoading(false));
  }, []);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const res = await hrAPI.getAttendanceReport({ start_date: startDate, end_date: endDate });
      setReportData(res.data);
    } catch { toast.error('Failed to load attendance report'); }
    finally { setReportLoading(false); }
  }, [startDate, endDate]);

  const loadLateReport = useCallback(async () => {
    setLateLoading(true);
    try {
      const res = await hrAPI.getLateReport({ month: lateMonth });
      setLateData(res.data);
    } catch { toast.error('Failed to load late report'); }
    finally { setLateLoading(false); }
  }, [lateMonth]);

  useEffect(() => { if (view === 'report') loadReport(); }, [view, loadReport]);
  useEffect(() => { if (view === 'late') loadLateReport(); }, [view, loadLateReport]);

  const loadNoPunchIn = useCallback(async () => {
    setNoPunchLoading(true);
    try {
      const res = await hrAPI.getNoPunchIn({ start_date: startDate, end_date: endDate });
      setNoPunchData(res.data);
    } catch { toast.error('Failed to load no punch-in data'); }
    finally { setNoPunchLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { if (view === 'nopunch') loadNoPunchIn(); }, [view, loadNoPunchIn]);

  const filteredRecords = clinicFilter === 'all' ? today.records : today.records.filter(r => r.clinic_id === clinicFilter);

  return (
    <div className="space-y-4">
      {/* Sub-navigation */}
      <div className="flex gap-1 bg-[#F8FAFC] p-1 rounded-lg w-fit" data-testid="attendance-view-switcher">
        {[['today', 'Today'], ['report', 'Daily Report'], ['late', 'Late Report'], ['nopunch', 'No Punch-In']].map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`att-view-${k}`}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${view === k ? 'bg-white text-[#0F4C81] font-semibold shadow-sm' : 'text-[#64748B] hover:text-[#0F4C81]'}`}>{label}</button>
        ))}
      </div>

      {view === 'today' && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <div className="stats-card" data-testid="att-total"><p className="text-xs text-[#64748B]">Checked In</p><p className="text-2xl font-bold">{today.summary?.total || 0}</p></div>
            <div className="stats-card"><p className="text-xs text-[#64748B]">Currently In</p><p className="text-2xl font-bold text-green-600">{today.summary?.checked_in || 0}</p></div>
            <div className="stats-card"><p className="text-xs text-[#64748B]">Completed</p><p className="text-2xl font-bold text-blue-600">{today.summary?.completed || 0}</p></div>
            <div className="stats-card"><p className="text-xs text-[#64748B]">Late Arrivals</p><p className="text-2xl font-bold text-red-600">{today.summary?.late || 0}</p></div>
          </div>
          <div className="flex items-center gap-3">
            <SearchableSelect value={clinicFilter || 'all'} onChange={setClinicFilter}
              options={[{ value: 'all', label: 'All Clinics' }, ...clinics.map(c => ({ value: c.id, label: c.name }))]}
              placeholder="All Clinics" testid="hr-clinic-filter" buttonClassName="w-56" />
            <Button variant="outline" size="sm" onClick={async () => {
              const res = await hrAPI.getTodayAttendance(clinicFilter !== 'all' ? { clinic_id: clinicFilter } : {});
              setToday(res.data);
            }}><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Employee</th><th className="px-4 py-3 text-left">Clinic</th><th className="px-4 py-3 text-center">Check In</th><th className="px-4 py-3 text-center">Check Out</th><th className="px-4 py-3 text-right">Hours</th><th className="px-4 py-3 text-center">Late</th><th className="px-4 py-3 text-center">Status</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">Loading...</td></tr> :
                  filteredRecords.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No attendance records for today</td></tr> :
                    filteredRecords.map(r => (
                      <tr key={r.id} className="border-t border-[#E2E8F0]">
                        <td className="px-4 py-3 font-medium">{r.employee_name || r.employee_number}</td>
                        <td className="px-4 py-3 text-sm">{r.clinic_name}</td>
                        <td className="px-4 py-3 text-center font-mono text-sm">{r.check_in_time}</td>
                        <td className="px-4 py-3 text-center font-mono text-sm">{r.check_out_time || '—'}</td>
                        <td className="px-4 py-3 text-right font-medium">{r.total_hours ? `${r.total_hours}h` : '—'}</td>
                        <td className="px-4 py-3 text-center">{r.is_late ? <span className="text-red-600 text-xs font-medium">{r.late_minutes}min late</span> : <span className="text-green-600 text-xs">On time</span>}</td>
                        <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'checked_in' ? 'bg-blue-100 text-blue-700' : r.status === 'full_day' ? 'bg-green-100 text-green-700' : r.status === 'half_day' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100'}`}>{r.status.replace('_', ' ')}</span></td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'report' && (
        <>
          <div className="flex items-end gap-3">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Start Date</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" data-testid="report-start-date" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">End Date</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" data-testid="report-end-date" />
            </div>
            <Button onClick={loadReport} size="sm" data-testid="report-load-btn"><ArrowsClockwise size={14} className="mr-1" /> Load Report</Button>
          </div>
          {reportLoading ? <p className="text-center py-8 text-[#64748B]">Loading report...</p> : reportData && (
            <>
              <div className="grid grid-cols-4 gap-4">
                <div className="stats-card" data-testid="report-total-records"><p className="text-xs text-[#64748B]">Total Records</p><p className="text-2xl font-bold">{reportData.overall?.total_records || 0}</p></div>
                <div className="stats-card"><p className="text-xs text-[#64748B]">Unique Employees</p><p className="text-2xl font-bold text-blue-600">{reportData.overall?.unique_employees || 0}</p></div>
                <div className="stats-card"><p className="text-xs text-[#64748B]">Total Late</p><p className="text-2xl font-bold text-red-600">{reportData.overall?.total_late || 0}</p></div>
                <div className="stats-card"><p className="text-xs text-[#64748B]">Avg Hours/Day</p><p className="text-2xl font-bold">{reportData.overall?.avg_hours || 0}h</p></div>
              </div>
              {/* Employee Summary */}
              <div>
                <h3 className="text-sm font-semibold text-[#0F4C81] mb-2">Employee Summary ({startDate} to {endDate})</h3>
                <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
                  <table className="w-full text-sm" data-testid="report-employee-summary">
                    <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Employee</th><th className="px-4 py-3 text-center">Days Present</th><th className="px-4 py-3 text-center">Late Count</th><th className="px-4 py-3 text-right">Total Hours</th><th className="px-4 py-3 text-right">Avg Hours</th></tr></thead>
                    <tbody>
                      {(reportData.employee_summary || []).length === 0
                        ? <tr><td colSpan={5} className="text-center py-8 text-[#64748B]">No attendance records found for this period</td></tr>
                        : (reportData.employee_summary || []).map(es => (
                          <tr key={es.employee_number} className="border-t border-[#E2E8F0]">
                            <td className="px-4 py-3"><span className="font-medium">{es.employee_name || es.employee_number}</span><span className="text-[#64748B] text-xs ml-2">({es.employee_number})</span></td>
                            <td className="px-4 py-3 text-center font-medium">{es.days_present}</td>
                            <td className="px-4 py-3 text-center">{es.late_count > 0 ? <span className="text-red-600 font-medium">{es.late_count}</span> : <span className="text-green-600">0</span>}</td>
                            <td className="px-4 py-3 text-right">{es.total_hours?.toFixed(1)}h</td>
                            <td className="px-4 py-3 text-right font-medium">{es.avg_hours}h</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Detailed Records */}
              <div>
                <h3 className="text-sm font-semibold text-[#0F4C81] mb-2">Detailed Records ({reportData.total || 0})</h3>
                <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm" data-testid="report-detailed-records">
                    <thead className="bg-[#F8FAFC] sticky top-0"><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-left">Clinic</th><th className="px-3 py-2 text-center">In</th><th className="px-3 py-2 text-center">Out</th><th className="px-3 py-2 text-right">Hours</th><th className="px-3 py-2 text-center">Late</th></tr></thead>
                    <tbody>
                      {(reportData.records || []).map(r => (
                        <tr key={r.id || `${r.employee_number}-${r.date}`} className="border-t border-[#E2E8F0] text-xs">
                          <td className="px-3 py-2 font-mono">{r.date}</td>
                          <td className="px-3 py-2">{r.employee_name || r.employee_number}</td>
                          <td className="px-3 py-2">{r.clinic_name}</td>
                          <td className="px-3 py-2 text-center font-mono">{r.check_in_time}</td>
                          <td className="px-3 py-2 text-center font-mono">{r.check_out_time || '—'}</td>
                          <td className="px-3 py-2 text-right">{r.total_hours ? `${r.total_hours}h` : '—'}</td>
                          <td className="px-3 py-2 text-center">{r.is_late ? <span className="text-red-600">{r.late_minutes}m</span> : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {view === 'late' && (
        <>
          <div className="flex items-end gap-3">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Month</label>
              <Input type="month" value={lateMonth} onChange={e => setLateMonth(e.target.value)} className="w-48" data-testid="late-month-picker" />
            </div>
            <Button onClick={loadLateReport} size="sm" data-testid="late-load-btn"><ArrowsClockwise size={14} className="mr-1" /> Load</Button>
          </div>
          {lateLoading ? <p className="text-center py-8 text-[#64748B]">Loading late report...</p> : lateData && (
            <>
              <div className="stats-card w-fit" data-testid="late-total"><p className="text-xs text-[#64748B]">Total Late Instances</p><p className="text-2xl font-bold text-red-600">{lateData.total_late_instances || 0}</p></div>
              <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="late-report-table">
                  <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Employee</th><th className="px-4 py-3 text-center">Late Count</th><th className="px-4 py-3 text-right">Total Late Minutes</th><th className="px-4 py-3 text-left">Late Dates</th><th className="px-4 py-3 text-left">Penalty</th></tr></thead>
                  <tbody>
                    {(lateData.late_employees || []).length === 0
                      ? <tr><td colSpan={5} className="text-center py-8 text-[#64748B]">No late records for this month</td></tr>
                      : (lateData.late_employees || []).map(emp => (
                        <tr key={emp.employee_number} className="border-t border-[#E2E8F0]">
                          <td className="px-4 py-3"><span className="font-medium">{emp.employee_name || emp.employee_number}</span><span className="text-[#64748B] text-xs ml-2">({emp.employee_number})</span></td>
                          <td className="px-4 py-3 text-center font-bold text-red-600">{emp.late_count}</td>
                          <td className="px-4 py-3 text-right">{emp.total_late_minutes} min</td>
                          <td className="px-4 py-3 text-xs text-[#64748B]">{(emp.dates || []).slice(0, 5).join(', ')}{emp.dates?.length > 5 ? ` +${emp.dates.length - 5} more` : ''}</td>
                          <td className="px-4 py-3">{(emp.penalties || []).length > 0
                            ? emp.penalties.map((p, i) => <span key={i} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded mr-1">{p.rule}</span>)
                            : <span className="text-xs text-green-600">None</span>
                          }</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {view === 'nopunch' && (
        <>
          <div className="flex items-end gap-3">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Start Date</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" data-testid="nopunch-start-date" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">End Date</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" data-testid="nopunch-end-date" />
            </div>
            <Button onClick={loadNoPunchIn} size="sm" data-testid="nopunch-load-btn"><ArrowsClockwise size={14} className="mr-1" /> Load</Button>
          </div>
          {noPunchLoading ? <p className="text-center py-8 text-[#64748B]">Loading...</p> : noPunchData && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="stats-card" data-testid="nopunch-total-employees"><p className="text-xs text-[#64748B]">Total Active Employees</p><p className="text-2xl font-bold">{noPunchData.total_employees || 0}</p></div>
                <div className="stats-card"><p className="text-xs text-[#64748B]">No Punch-In</p><p className="text-2xl font-bold text-red-600">{noPunchData.total_absent || 0}</p></div>
                {noPunchData.working_days && <div className="stats-card"><p className="text-xs text-[#64748B]">Working Days</p><p className="text-2xl font-bold text-blue-600">{noPunchData.working_days}</p></div>}
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="nopunch-table">
                  <thead className="bg-[#F8FAFC]">
                    <tr>
                      <th className="px-4 py-3 text-left">Employee</th>
                      <th className="px-4 py-3 text-left">Department</th>
                      <th className="px-4 py-3 text-left">Location</th>
                      {noPunchData.working_days && <><th className="px-4 py-3 text-center">Days Present</th><th className="px-4 py-3 text-center">Days Absent</th><th className="px-4 py-3 text-right">Attendance %</th></>}
                    </tr>
                  </thead>
                  <tbody>
                    {(noPunchData.absent_employees || []).length === 0
                      ? <tr><td colSpan={6} className="text-center py-8 text-green-600 font-medium">All employees have punched in!</td></tr>
                      : (noPunchData.absent_employees || []).map(e => (
                        <tr key={e.employee_number} className="border-t border-[#E2E8F0]">
                          <td className="px-4 py-3"><span className="font-medium">{e.employee_name || e.employee_number}</span><span className="text-[#64748B] text-xs ml-2">({e.employee_number})</span></td>
                          <td className="px-4 py-3">{e.department || '—'}</td>
                          <td className="px-4 py-3">{e.location || '—'}</td>
                          {noPunchData.working_days && (
                            <>
                              <td className="px-4 py-3 text-center">{e.days_present}</td>
                              <td className="px-4 py-3 text-center font-bold text-red-600">{e.days_absent}</td>
                              <td className="px-4 py-3 text-right"><span className={`px-2 py-0.5 rounded text-xs font-medium ${e.attendance_pct >= 80 ? 'bg-green-100 text-green-700' : e.attendance_pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{e.attendance_pct}%</span></td>
                            </>
                          )}
                        </tr>
                      ))

// ─── Reimbursement Tab ──────────────────────────────────────────────────────
                    }
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
function ReimbursementTab() {
  const [claims, setClaims] = useState([]);
  const [totals, setTotals] = useState({});
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_number: '', category: '', amount: '', description: '', receipt_date: '', vendor_name: '' });
  const [payDialog, setPayDialog] = useState(null); // { id, date }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const [claimRes, empRes] = await Promise.all([
        hrAPI.getReimbursements(params),
        hrAPI.getEmployees({ page_size: 200 }),
      ]);
      setClaims(claimRes.data.claims || []);
      setTotals(claimRes.data.totals || {});
      setCategories(claimRes.data.categories || []);
      setTotalPages(claimRes.data.total_pages || 1);
      setEmployees(empRes.data.employees || []);
    } catch { toast.error('Failed to load reimbursements'); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    try {
      await hrAPI.createReimbursement({ ...form, amount: parseFloat(form.amount) });
      toast.success('Reimbursement claim submitted');
      setAddDialog(false);
      setForm({ employee_number: '', category: '', amount: '', description: '', receipt_date: '', vendor_name: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit claim'); }
  };

  const handleAction = async (id, action) => {
    try {
      if (action === 'approve') await hrAPI.approveReimbursement(id);
      else if (action === 'reject') await hrAPI.rejectReimbursement(id, '');
      else if (action === 'pay') await hrAPI.markReimbursementPaid(id);
      toast.success(`Claim ${action === 'pay' ? 'marked paid' : action + 'd'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
  };

  const confirmPay = async () => {
    try {
      await hrAPI.markReimbursementPaid(payDialog.id, payDialog.date);
      toast.success('Claim marked paid');
      setPayDialog(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const pendingAmt = totals.pending?.amount || 0;
  const approvedAmt = totals.approved?.amount || 0;
  const paidAmt = totals.paid?.amount || 0;

  return (
    <div className="space-y-4" data-testid="reimbursement-tab">
      <div className="grid grid-cols-4 gap-4">
        <div className="stats-card"><p className="text-xs text-[#64748B]">Pending</p><p className="text-2xl font-bold text-amber-600">{fmt(pendingAmt)}</p><p className="text-xs text-[#64748B]">{totals.pending?.count || 0} claims</p></div>
        <div className="stats-card"><p className="text-xs text-[#64748B]">Approved</p><p className="text-2xl font-bold text-green-600">{fmt(approvedAmt)}</p><p className="text-xs text-[#64748B]">{totals.approved?.count || 0} claims</p></div>
        <div className="stats-card"><p className="text-xs text-[#64748B]">Paid</p><p className="text-2xl font-bold text-blue-600">{fmt(paidAmt)}</p><p className="text-xs text-[#64748B]">{totals.paid?.count || 0} claims</p></div>
        <div className="stats-card"><p className="text-xs text-[#64748B]">Total Claims</p><p className="text-2xl font-bold">{(totals.pending?.count || 0) + (totals.approved?.count || 0) + (totals.paid?.count || 0) + (totals.rejected?.count || 0)}</p></div>
      </div>
      <div className="flex items-center justify-between">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setAddDialog(true)} data-testid="add-reimbursement-btn"><Plus size={14} className="mr-2" /> New Claim</Button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Employee</th><th className="px-4 py-3 text-left">Category</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-left">Description</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Credited On</th><th className="px-4 py-3 text-center">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">Loading...</td></tr> :
              claims.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">No reimbursement claims</td></tr> :
                claims.map(c => (
                  <tr key={c.id} className="border-t border-[#E2E8F0]">
                    <td className="px-4 py-3"><p className="font-medium">{c.employee_name || c.employee_number}</p><p className="text-xs text-[#64748B]">{c.department}</p></td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 bg-[#F8FAFC] rounded text-xs">{c.category}</span></td>
                    <td className="px-4 py-3 text-right font-semibold">{fmt(c.amount)}</td>
                    <td className="px-4 py-3 max-w-[200px] truncate text-[#64748B] text-xs">{c.description || '—'}</td>
                    <td className="px-4 py-3 text-sm">{c.receipt_date || c.created_at?.split('T')[0]}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${c.status === 'approved' ? 'bg-green-100 text-green-700' : c.status === 'paid' ? 'bg-blue-100 text-blue-700' : c.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{c.status}</span>{c.status === 'pending' && c.approval_stage === 'manager' && <span className="block text-[10px] text-[#64748B] mt-0.5">Awaiting manager</span>}{c.status === 'pending' && c.approval_stage !== 'manager' && <span className="block text-[10px] text-[#64748B] mt-0.5">Ready for HR</span>}{c.manager_approved_by && <span className="block text-[10px] text-green-600 mt-0.5">Mgr: {c.manager_approved_by}</span>}</td>
                    <td className="px-4 py-3 text-sm text-[#64748B]" data-testid={`credited-${c.id}`}>{c.credited_date || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex gap-1 justify-center">
                        {c.status === 'pending' && c.approval_stage === 'manager' && (
                          <span className="text-[11px] text-[#94A3B8] italic">Pending manager</span>
                        )}
                        {c.status === 'pending' && c.approval_stage !== 'manager' && (<>
                          <Button variant="outline" size="sm" onClick={() => handleAction(c.id, 'approve')} className="text-green-600 h-7 px-2" data-testid={`approve-claim-${c.id}`}><Check size={14} /></Button>
                          <Button variant="outline" size="sm" onClick={() => handleAction(c.id, 'reject')} className="text-red-600 h-7 px-2"><X size={14} /></Button>
                        </>)}
                        {c.status === 'approved' && (
                          <Button variant="outline" size="sm" onClick={() => setPayDialog({ id: c.id, date: new Date().toISOString().slice(0, 10) })} className="text-blue-600 h-7 px-2" data-testid={`pay-claim-${c.id}`}><CurrencyDollar size={14} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-sm text-[#64748B] mx-2">Page {page}/{totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Reimbursement Claim</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <SearchableSelect value={form.employee_number} onChange={(v) => setForm(f => ({...f, employee_number: v}))} placeholder="Select employee" testid="" buttonClassName="w-full"
                  options={[...employees.map(e => ({ value: e.employee_number, label: `${e.display_name} (${e.employee_number})` }))]} />
            <SearchableSelect value={form.category} onChange={(v) => setForm(f => ({...f, category: v}))} placeholder="Category" testid="" buttonClassName="w-full"
                  options={[...categories.map(c => ({ value: c.code, label: c.name }))]} />
            <Input type="number" placeholder="Amount (INR)" value={form.amount} onChange={(e) => setForm(f => ({...f, amount: e.target.value}))} />
            <Input placeholder="Description" value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#64748B]">Receipt Date</label><Input type="date" value={form.receipt_date} onChange={(e) => setForm(f => ({...f, receipt_date: e.target.value}))} /></div>
              <div><label className="text-xs text-[#64748B]">Vendor</label><Input placeholder="Vendor" value={form.vendor_name} onChange={(e) => setForm(f => ({...f, vendor_name: e.target.value}))} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={handleSubmit} disabled={!form.employee_number || !form.category || !form.amount} data-testid="submit-reimbursement-btn">Submit Claim</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog — capture credited-to-bank date */}
      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Mark Claim Paid</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-[#64748B]">Date credited to employee's bank</label>
            <Input type="date" value={payDialog?.date || ''} onChange={(e) => setPayDialog(p => ({ ...p, date: e.target.value }))} data-testid="credited-date-input" />
          </div>
          <DialogFooter><Button onClick={confirmPay} disabled={!payDialog?.date} data-testid="confirm-pay-btn">Confirm Paid</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Assets Tab ─────────────────────────────────────────────────────────────
function AssetsTab() {
  const [assets, setAssets] = useState([]);
  const [typeSummary, setTypeSummary] = useState({});
  const [assetTypes, setAssetTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_number: '', asset_type: '', asset_name: '', asset_tag: '', serial_number: '', make_model: '', issued_date: '', notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (typeFilter !== 'all') params.asset_type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const [assetRes, empRes] = await Promise.all([
        hrAPI.getAssets(params),
        hrAPI.getEmployees({ page_size: 200 }),
      ]);
      setAssets(assetRes.data.assets || []);
      setTypeSummary(assetRes.data.type_summary || {});
      setAssetTypes(assetRes.data.asset_types || []);
      setTotalPages(assetRes.data.total_pages || 1);
      setTotal(assetRes.data.total || 0);
      setEmployees(empRes.data.employees || []);
    } catch { toast.error('Failed to load assets'); }
    finally { setLoading(false); }
  }, [page, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    try {
      await hrAPI.createAsset(form);
      toast.success('Asset allocated');
      setAddDialog(false);
      setForm({ employee_number: '', asset_type: '', asset_name: '', asset_tag: '', serial_number: '', make_model: '', issued_date: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to allocate asset'); }
  };

  const handleReturn = async (id) => {
    try {
      await hrAPI.returnAsset(id, 'good');
      toast.success('Asset marked as returned');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="assets-tab">
      <div className="flex flex-wrap gap-3">
        {assetTypes.map(t => (
          <div key={t.code} className="bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-center min-w-[90px] cursor-pointer hover:border-[#0F4C81]" onClick={() => setTypeFilter(t.code === typeFilter ? 'all' : t.code)}>
            <p className="text-lg font-bold">{typeSummary[t.code] || 0}</p>
            <p className="text-xs text-[#64748B]">{t.name}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="issued">Issued</SelectItem>
              <SelectItem value="returned">Returned</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-[#64748B] self-center">{total} assets</span>
        </div>
        <Button onClick={() => setAddDialog(true)} data-testid="add-asset-btn"><Plus size={14} className="mr-2" /> Allocate Asset</Button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Employee</th><th className="px-4 py-3 text-left">Asset</th><th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-left">Serial / Tag</th><th className="px-4 py-3 text-left">Issued</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-center">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">Loading...</td></tr> :
              assets.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No assets found</td></tr> :
                assets.map(a => (
                  <tr key={a.id} className="border-t border-[#E2E8F0]">
                    <td className="px-4 py-3"><p className="font-medium">{a.employee_name || a.employee_number}</p><p className="text-xs text-[#64748B]">{a.department}</p></td>
                    <td className="px-4 py-3"><p className="font-medium">{a.asset_name}</p><p className="text-xs text-[#64748B]">{a.make_model || '—'}</p></td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 bg-[#F8FAFC] rounded text-xs">{a.asset_type}</span></td>
                    <td className="px-4 py-3 text-sm font-mono">{a.serial_number || a.asset_tag || '—'}</td>
                    <td className="px-4 py-3 text-sm">{a.issued_date || '—'}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${a.status === 'issued' ? 'bg-green-100 text-green-700' : a.status === 'returned' ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'}`}>{a.status}</span></td>
                    <td className="px-4 py-3 text-center">
                      {a.status === 'issued' && (
                        <Button variant="outline" size="sm" onClick={() => handleReturn(a.id)} className="h-7 px-2 text-amber-600" data-testid={`return-asset-${a.id}`}>Return</Button>
                      )}
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-sm text-[#64748B] mx-2">Page {page}/{totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Allocate Asset</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <SearchableSelect value={form.employee_number} onChange={(v) => setForm(f => ({...f, employee_number: v}))} placeholder="Select employee" testid="" buttonClassName="w-full"
                  options={[...employees.map(e => ({ value: e.employee_number, label: `${e.display_name} (${e.employee_number})` }))]} />
            <SearchableSelect value={form.asset_type} onChange={(v) => setForm(f => ({...f, asset_type: v}))} placeholder="Asset type" testid="" buttonClassName="w-full"
                  options={[...assetTypes.map(t => ({ value: t.code, label: t.name }))]} />
            <Input placeholder="Asset name (e.g. Dell Latitude 5520)" value={form.asset_name} onChange={(e) => setForm(f => ({...f, asset_name: e.target.value}))} />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Asset tag" value={form.asset_tag} onChange={(e) => setForm(f => ({...f, asset_tag: e.target.value}))} />
              <Input placeholder="Serial number" value={form.serial_number} onChange={(e) => setForm(f => ({...f, serial_number: e.target.value}))} />
            </div>
            <Input placeholder="Make / Model" value={form.make_model} onChange={(e) => setForm(f => ({...f, make_model: e.target.value}))} />
            <div><label className="text-xs text-[#64748B]">Issued Date</label><Input type="date" value={form.issued_date} onChange={(e) => setForm(f => ({...f, issued_date: e.target.value}))} /></div>
            <Input placeholder="Notes" value={form.notes} onChange={(e) => setForm(f => ({...f, notes: e.target.value}))} />
          </div>
          <DialogFooter><Button onClick={handleSubmit} disabled={!form.employee_number || !form.asset_type || !form.asset_name} data-testid="submit-asset-btn">Allocate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Config Tab ─────────────────────────────────────────────────────────────
function ConfigTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editClinic, setEditClinic] = useState(null);
  const [clinicForm, setClinicForm] = useState({ name: '', latitude: '', longitude: '', radius_meters: 150, shift_start: '09:30', shift_end: '18:30', grace_minutes: 15 });
  const [newLeave, setNewLeave] = useState({ name: '', code: '', annual_quota: 0, is_paid: true, carry_forward: false });
  const [newPenalty, setNewPenalty] = useState({ late_count_threshold: 3, leave_type_code: 'CL', deduction_amount: 0.5, period: 'month' });

  const load = async () => {
    setLoading(true);
    try { const res = await hrAPI.getConfig(); setConfig(res.data); }
    catch { toast.error('Failed to load config'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSaveClinic = async () => {
    try {
      const data = { ...clinicForm, latitude: parseFloat(clinicForm.latitude), longitude: parseFloat(clinicForm.longitude), radius_meters: parseInt(clinicForm.radius_meters), grace_minutes: parseInt(clinicForm.grace_minutes), address: '', is_active: true };
      if (editClinic?.id) await hrAPI.updateClinicLocation(editClinic.id, data);
      else await hrAPI.createClinicLocation(data);
      toast.success('Clinic saved');
      setEditClinic(null);
      load();
    } catch { toast.error('Failed to save clinic'); }
  };

  const handleAddLeaveType = async () => {
    try {
      await hrAPI.createLeaveType({ ...newLeave, annual_quota: parseFloat(newLeave.annual_quota), is_active: true });
      toast.success('Leave type added');
      setNewLeave({ name: '', code: '', annual_quota: 0, is_paid: true, carry_forward: false });
      load();
    } catch { toast.error('Failed to add leave type'); }
  };

  const handleAddPenalty = async () => {
    try {
      await hrAPI.createLatePenalty({ ...newPenalty, late_count_threshold: parseInt(newPenalty.late_count_threshold), deduction_amount: parseFloat(newPenalty.deduction_amount), penalty_type: 'leave_deduction', is_active: true });
      toast.success('Penalty rule added');
      load();
    } catch { toast.error('Failed to add penalty'); }
  };

  if (loading) return <div className="text-center py-8 text-[#64748B]">Loading configuration...</div>;
  if (!config) return null;

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="config-leave-types">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><CalendarBlank size={18} /> Leave Types</h3>
        <div className="space-y-2 mb-3">
          {config.leave_types.map(lt => (
            <div key={lt.code} className="flex items-center justify-between bg-[#F8FAFC] rounded-lg px-4 py-2">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs bg-white px-2 py-0.5 rounded border">{lt.code}</span>
                <span className="font-medium text-sm">{lt.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm">{lt.annual_quota} days/yr</span>
                <span className="text-xs text-[#64748B]">{lt.is_paid ? 'Paid' : 'Unpaid'}</span>
                <Button variant="ghost" size="sm" onClick={async () => { await hrAPI.deleteLeaveType(lt.code); load(); }} className="h-6 w-6 p-0 text-red-500"><Trash size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Code" value={newLeave.code} onChange={(e) => setNewLeave(f => ({...f, code: e.target.value.toUpperCase()}))} className="w-24" />
          <Input placeholder="Name" value={newLeave.name} onChange={(e) => setNewLeave(f => ({...f, name: e.target.value}))} className="flex-1" />
          <Input type="number" placeholder="Quota" value={newLeave.annual_quota} onChange={(e) => setNewLeave(f => ({...f, annual_quota: e.target.value}))} className="w-20" />
          <Button size="sm" onClick={handleAddLeaveType} disabled={!newLeave.code || !newLeave.name}><Plus size={14} className="mr-1" /> Add</Button>
        </div>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="config-clinics">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><MapPinLine size={18} /> Clinic Locations ({config.clinic_locations.length})</h3>
        <div className="max-h-[300px] overflow-y-auto space-y-1 mb-3">
          {config.clinic_locations.map(c => (
            <div key={c.id} className="flex items-center justify-between bg-[#F8FAFC] rounded px-3 py-2 text-sm">
              <div className="flex items-center gap-2">
                <MapPin size={14} className={c.needs_gps_config ? 'text-amber-500' : 'text-green-600'} />
                <span className="font-medium">{c.name}</span>
                {c.needs_gps_config && <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">GPS not set</span>}
              </div>
              <div className="flex items-center gap-2 text-xs text-[#64748B]">
                <span>{c.shift_start}-{c.shift_end}</span>
                <span>{c.radius_meters}m</span>
                <Button variant="ghost" size="sm" onClick={() => { setEditClinic(c); setClinicForm({ name: c.name, latitude: c.latitude, longitude: c.longitude, radius_meters: c.radius_meters, shift_start: c.shift_start, shift_end: c.shift_end, grace_minutes: c.grace_minutes }); }} className="h-6 px-2 text-blue-600">Edit</Button>
              </div>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => { setEditClinic({}); setClinicForm({ name: '', latitude: '', longitude: '', radius_meters: 150, shift_start: '09:30', shift_end: '18:30', grace_minutes: 15 }); }}><Plus size={14} className="mr-1" /> Add Clinic</Button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="config-penalties">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Warning size={18} /> Late Coming Penalty Rules</h3>
        <div className="space-y-2 mb-3">
          {config.late_penalties.map(p => (
            <div key={p.id} className="flex items-center justify-between bg-[#F8FAFC] rounded-lg px-4 py-2 text-sm">
              <span>{p.late_count_threshold} lates per {p.period} → Deduct {p.deduction_amount} {p.leave_type_code}</span>
              <Button variant="ghost" size="sm" onClick={async () => { await hrAPI.deleteLatePenalty(p.id); load(); }} className="h-6 w-6 p-0 text-red-500"><Trash size={14} /></Button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input type="number" placeholder="Lates" value={newPenalty.late_count_threshold} onChange={(e) => setNewPenalty(f => ({...f, late_count_threshold: e.target.value}))} className="w-20" />
          <span className="text-sm text-[#64748B]">lates/month →</span>
          <Input type="number" placeholder="Deduct" value={newPenalty.deduction_amount} onChange={(e) => setNewPenalty(f => ({...f, deduction_amount: e.target.value}))} className="w-20" step="0.5" />
          <Select value={newPenalty.leave_type_code} onValueChange={(v) => setNewPenalty(f => ({...f, leave_type_code: v}))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{config.leave_types.map(l => <SelectItem key={l.code} value={l.code}>{l.code}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={handleAddPenalty}><Plus size={14} className="mr-1" /> Add</Button>
        </div>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="config-docs">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><FileText size={18} /> Document Categories</h3>
        <div className="flex flex-wrap gap-2">
          {config.document_categories.map(c => (
            <span key={c.code} className="px-3 py-1.5 bg-[#F8FAFC] rounded text-sm flex items-center gap-1.5">{c.name} {c.is_required && <span className="text-red-500 text-xs">*</span>}</span>
          ))}
        </div>
      </div>
      <Dialog open={!!editClinic} onOpenChange={(o) => { if (!o) setEditClinic(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editClinic?.id ? 'Edit' : 'Add'} Clinic Location</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Clinic name" value={clinicForm.name} onChange={(e) => setClinicForm(f => ({...f, name: e.target.value}))} />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#64748B]">Latitude</label><Input type="number" step="0.0001" value={clinicForm.latitude} onChange={(e) => setClinicForm(f => ({...f, latitude: e.target.value}))} /></div>
              <div><label className="text-xs text-[#64748B]">Longitude</label><Input type="number" step="0.0001" value={clinicForm.longitude} onChange={(e) => setClinicForm(f => ({...f, longitude: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-[#64748B]">Radius (m)</label><Input type="number" value={clinicForm.radius_meters} onChange={(e) => setClinicForm(f => ({...f, radius_meters: e.target.value}))} /></div>
              <div><label className="text-xs text-[#64748B]">Shift Start</label><Input type="time" value={clinicForm.shift_start} onChange={(e) => setClinicForm(f => ({...f, shift_start: e.target.value}))} /></div>
              <div><label className="text-xs text-[#64748B]">Shift End</label><Input type="time" value={clinicForm.shift_end} onChange={(e) => setClinicForm(f => ({...f, shift_end: e.target.value}))} /></div>
            </div>
            <div><label className="text-xs text-[#64748B]">Grace Period (min)</label><Input type="number" value={clinicForm.grace_minutes} onChange={(e) => setClinicForm(f => ({...f, grace_minutes: e.target.value}))} /></div>
          </div>
          <DialogFooter><Button onClick={handleSaveClinic}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DEPARTMENT / LOCATION DRILL-DOWN VIEW
// ═══════════════════════════════════════════════════════════════════════════════
function DrillDownView({ type, value, onBack, onSelectEmployee }) {
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetchFn = type === 'department' ? hrAPI.getEmployeesByDepartment : hrAPI.getEmployeesByLocation;
    fetchFn(value, { page_size: 100 })
      .then(res => { setEmployees(res.data.employees || []); setTotal(res.data.total || 0); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [type, value]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="drilldown-back-btn"><ArrowLeft size={16} className="mr-1" /> Back to Dashboard</Button>
        <h3 className="font-semibold text-lg">{value} <span className="text-sm font-normal text-[#64748B]">({total} employees)</span></h3>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Employee</th><th className="px-4 py-3 text-left">Department</th><th className="px-4 py-3 text-left">Location</th><th className="px-4 py-3 text-left">Job Title</th><th className="px-4 py-3 text-left">Status</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-[#64748B]">Loading...</td></tr> :
              employees.map(emp => (
                <tr key={emp.employee_number} className="border-t border-[#E2E8F0] cursor-pointer hover:bg-[#F8FAFC]" onClick={() => onSelectEmployee(emp.employee_number)}>
                  <td className="px-4 py-3"><p className="font-medium">{emp.display_name}</p><p className="text-xs text-[#64748B]">{emp.employee_number}</p></td>
                  <td className="px-4 py-3">{emp.department || '—'}</td>
                  <td className="px-4 py-3 text-sm">{emp.location?.replace('HearClear ', '') || '—'}</td>
                  <td className="px-4 py-3 text-sm">{emp.job_title || '—'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${emp.employment_status === 'Working' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{emp.employment_status}</span></td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// REGULARIZATIONS TAB (HR Admin)
// ═══════════════════════════════════════════════════════════════════════════════
function RegularizationsTab() {
  const [regs, setRegs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrAPI.getRegularizations({ status: statusFilter !== 'all' ? statusFilter : undefined });
      setRegs(res.data.regularizations || []);
    } catch { toast.error('Failed to load regularizations'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    try {
      if (action === 'approve') {
        await hrAPI.approveRegularization(id);
        toast.success('Regularization approved — attendance record created');
      } else {
        await hrAPI.rejectRegularization(id);
        toast.success('Regularization rejected');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-4" data-testid="regularizations-tab">
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="reg-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load}><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        <p className="text-sm text-[#64748B] ml-auto">{regs.length} request(s)</p>
      </div>

      {loading ? <div className="text-center py-12 text-[#64748B]">Loading...</div> : regs.length === 0 ? (
        <div className="text-center py-12 text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl">No regularization requests found</div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="regularizations-table">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="px-4 py-3 text-left">Employee</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Time</th>
                <th className="px-4 py-3 text-right">Hours</th>
                <th className="px-4 py-3 text-left">Reason</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {regs.map(r => (
                <tr key={r.id} className="border-t border-[#E2E8F0]" data-testid={`reg-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium">{r.employee_name}</p>
                    <p className="text-xs text-[#64748B]">{r.employee_number} | {r.department}</p>
                  </td>
                  <td className="px-4 py-3">{r.date}</td>
                  <td className="px-4 py-3 text-xs">{r.check_in_time} — {r.check_out_time}</td>
                  <td className="px-4 py-3 text-right font-medium">{r.total_hours}h</td>
                  <td className="px-4 py-3 text-xs text-[#64748B] max-w-[200px] truncate">{r.reason}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {r.status === 'pending' ? (
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-green-700 hover:bg-green-50"
                          onClick={() => handleAction(r.id, 'approve')} data-testid={`approve-reg-${r.id}`}>
                          <Check size={14} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50"
                          onClick={() => handleAction(r.id, 'reject')} data-testid={`reject-reg-${r.id}`}>
                          <X size={14} className="mr-1" /> Reject
                        </Button>
                      </div>
                    ) : <span className="text-xs text-[#64748B]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HR PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function HRPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [empTotal, setEmpTotal] = useState(0);
  const [empPage, setEmpPage] = useState(1);
  const [empPages, setEmpPages] = useState(1);
  const [payroll, setPayroll] = useState([]);
  const [payrollTotal, setPayrollTotal] = useState(0);
  const [payrollPage, setPayrollPage] = useState(1);
  const [payrollPages, setPayrollPages] = useState(1);
  const [payrollTotals, setPayrollTotals] = useState({});
  const [payrollMonths, setPayrollMonths] = useState([]);
  const [filters, setFilters] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillDown, setDrillDown] = useState(null); // { type: 'department'|'location', value: 'name' }

  useEffect(() => {
    Promise.all([hrAPI.getDashboard(), hrAPI.getFilters()])
      .then(([dashRes, filterRes]) => { setDashboard(dashRes.data); setFilters(filterRes.data); })
      .catch(() => toast.error('Failed to load HR data'))
      .finally(() => setLoading(false));
  }, []);

  const loadEmployees = useCallback(async (page = 1) => {
    const params = { page, page_size: 50 };
    if (searchTerm) params.search = searchTerm;
    if (deptFilter !== 'all') params.department = deptFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    const res = await hrAPI.getEmployees(params);
    setEmployees(res.data.employees); setEmpTotal(res.data.total); setEmpPage(res.data.page); setEmpPages(res.data.total_pages);
  }, [searchTerm, deptFilter, statusFilter]);

  useEffect(() => { if (activeTab === 'employees') loadEmployees(1); }, [activeTab, loadEmployees]);

  const loadPayroll = useCallback(async (page = 1) => {
    const params = { page, page_size: 50 };
    if (monthFilter !== 'all') params.month = monthFilter;
    if (searchTerm && activeTab === 'payroll') params.search = searchTerm;
    if (deptFilter !== 'all') params.department = deptFilter;
    const res = await hrAPI.getPayroll(params);
    setPayroll(res.data.records); setPayrollTotal(res.data.total); setPayrollPage(res.data.page); setPayrollPages(res.data.total_pages);
    setPayrollTotals(res.data.totals); setPayrollMonths(res.data.available_months);
  }, [monthFilter, searchTerm, deptFilter, activeTab]);

  useEffect(() => { if (activeTab === 'payroll') loadPayroll(1); }, [activeTab, loadPayroll]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="hr-page">
      <PageHeader title="HR & Payroll" subtitle="Employee master, payroll, leave, attendance, reimbursements, assets" />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="dashboard" data-testid="hr-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="employees" data-testid="hr-tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="hr-tab-payroll">Payroll</TabsTrigger>
          <TabsTrigger value="leave" data-testid="hr-tab-leave">Leave</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="hr-tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="reimbursements" data-testid="hr-tab-reimbursements"><Receipt size={14} className="mr-1" /> Reimbursements</TabsTrigger>
          <TabsTrigger value="assets" data-testid="hr-tab-assets"><Laptop size={14} className="mr-1" /> Assets</TabsTrigger>
          <TabsTrigger value="regularizations" data-testid="hr-tab-regularizations"><ArrowCounterClockwise size={14} className="mr-1" /> Regularizations</TabsTrigger>
          <TabsTrigger value="config" data-testid="hr-tab-config"><Gear size={14} className="mr-1" /> Config</TabsTrigger>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard">
          {drillDown ? (
            <DrillDownView
              type={drillDown.type}
              value={drillDown.value}
              onBack={() => setDrillDown(null)}
              onSelectEmployee={(en) => setSelectedEmployee(en)}
            />
          ) : (
            loading ? <div className="text-center py-12 text-[#64748B]">Loading...</div> : dashboard && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="stats-card"><div className="flex items-center gap-2 mb-1"><Users size={18} className="text-[#0F4C81]" /><span className="text-xs text-[#64748B]">Total Employees</span></div><p className="text-2xl font-bold">{dashboard.total_employees}</p></div>
                  <div className="stats-card"><div className="flex items-center gap-2 mb-1"><IdentificationBadge size={18} className="text-green-600" /><span className="text-xs text-[#64748B]">Active</span></div><p className="text-2xl font-bold text-green-600">{dashboard.active_employees}</p></div>
                  {dashboard.latest_payroll && (<>
                    <div className="stats-card"><div className="flex items-center gap-2 mb-1"><Money size={18} className="text-[#0F4C81]" /><span className="text-xs text-[#64748B]">Latest Gross ({dashboard.latest_payroll.month})</span></div><p className="text-2xl font-bold">{fmt(dashboard.latest_payroll.total_gross)}</p></div>
                    <div className="stats-card"><div className="flex items-center gap-2 mb-1"><Money size={18} className="text-green-600" /><span className="text-xs text-[#64748B]">Latest Net Pay</span></div><p className="text-2xl font-bold text-green-600">{fmt(dashboard.latest_payroll.total_net)}</p></div>
                  </>)}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Departments - CLICKABLE */}
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                    <h3 className="font-semibold text-sm text-[#64748B] mb-3 flex items-center gap-2"><Buildings size={16} /> Departments</h3>
                    <div className="space-y-2">{dashboard.departments.map(d => (
                      <div key={d.name}
                        className="flex items-center justify-between cursor-pointer hover:bg-[#F8FAFC] rounded px-2 py-1 -mx-2 transition-colors"
                        onClick={() => setDrillDown({ type: 'department', value: d.name })}
                        data-testid={`dept-drill-${d.name}`}
                      >
                        <span className="text-sm flex items-center gap-1">{d.name} <CaretRight size={12} className="text-[#64748B]" /></span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-[#F8FAFC] rounded-full h-2"><div className="bg-[#0F4C81] h-2 rounded-full" style={{ width: `${(d.count / dashboard.active_employees * 100)}%` }} /></div>
                          <span className="text-sm font-semibold w-8 text-right">{d.count}</span>
                        </div>
                      </div>
                    ))}</div>
                  </div>
                  {/* Locations - CLICKABLE */}
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                    <h3 className="font-semibold text-sm text-[#64748B] mb-3 flex items-center gap-2"><MapPin size={16} /> Locations</h3>
                    <div className="space-y-2">{dashboard.locations.map(l => (
                      <div key={l.name}
                        className="flex items-center justify-between cursor-pointer hover:bg-[#F8FAFC] rounded px-2 py-1 -mx-2 transition-colors"
                        onClick={() => setDrillDown({ type: 'location', value: l.name })}
                        data-testid={`loc-drill-${l.name}`}
                      >
                        <span className="text-sm truncate max-w-[200px] flex items-center gap-1">{l.name} <CaretRight size={12} className="text-[#64748B]" /></span>
                        <span className="text-sm font-semibold">{l.count}</span>
                      </div>
                    ))}</div>
                  </div>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                  <h3 className="font-semibold text-sm text-[#64748B] mb-4 flex items-center gap-2"><Money size={16} /> Monthly Payroll Trend</h3>
                  <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-[#F8FAFC]"><tr><th className="px-3 py-2 text-left">Month</th><th className="px-3 py-2 text-right">Employees</th><th className="px-3 py-2 text-right">Gross</th><th className="px-3 py-2 text-right">PF</th><th className="px-3 py-2 text-right">Tax</th><th className="px-3 py-2 text-right">Net Pay</th></tr></thead><tbody>{dashboard.payroll_trend.map((p, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="px-3 py-2 font-medium">{p.month}</td><td className="px-3 py-2 text-right">{p.employee_count}</td><td className="px-3 py-2 text-right font-semibold">{fmtK(p.total_gross)}</td><td className="px-3 py-2 text-right text-amber-600">{fmtK(p.total_pf)}</td><td className="px-3 py-2 text-right text-red-600">{fmtK(p.total_tax)}</td><td className="px-3 py-2 text-right font-bold text-[#0F172A]">{fmtK(p.total_net)}</td></tr>))}</tbody></table></div>
                </div>
              </div>
            )
          )}
        </TabsContent>

        {/* Employees */}
        <TabsContent value="employees">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md"><MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" /><Input placeholder="Search by name, ID, email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadEmployees(1)} className="pl-9" data-testid="emp-search-input" /></div>
            {filters && (<><Select value={deptFilter} onValueChange={(v) => { setDeptFilter(v); setEmpPage(1); }}><SelectTrigger className="w-44"><SelectValue placeholder="All Departments" /></SelectTrigger><SelectContent><SelectItem value="all">All Departments</SelectItem>{filters.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select><Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setEmpPage(1); }}><SelectTrigger className="w-36"><SelectValue placeholder="All Statuses" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{filters.statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></>)}
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden"><table className="w-full text-sm"><thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Employee</th><th className="px-4 py-3 text-left">Department</th><th className="px-4 py-3 text-left">Location</th><th className="px-4 py-3 text-left">Job Title</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Joined</th></tr></thead><tbody>{employees.map(emp => (<tr key={emp.employee_number} className="border-t border-[#E2E8F0] cursor-pointer hover:bg-[#F8FAFC]" onClick={() => setSelectedEmployee(emp.employee_number)} data-testid={`emp-row-${emp.employee_number}`}><td className="px-4 py-3"><div><p className="font-medium">{emp.display_name}</p><p className="text-xs text-[#64748B]">{emp.employee_number}</p></div></td><td className="px-4 py-3">{emp.department || '—'}</td><td className="px-4 py-3 text-sm">{emp.location?.replace('HearClear ', '') || '—'}</td><td className="px-4 py-3 text-sm truncate max-w-[200px]">{emp.job_title || '—'}</td><td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${emp.employment_status === 'Working' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{emp.employment_status}</span></td><td className="px-4 py-3 text-sm text-[#64748B]">{emp.date_joined || '—'}</td></tr>))}</tbody></table></div>
          {empPages > 1 && (<div className="flex items-center justify-between mt-3"><p className="text-sm text-[#64748B]">Page {empPage}/{empPages} ({empTotal} employees)</p><div className="flex gap-1"><Button variant="outline" size="sm" disabled={empPage<=1} onClick={() => { setEmpPage(1); loadEmployees(1); }}>First</Button><Button variant="outline" size="sm" disabled={empPage<=1} onClick={() => { const p=empPage-1; setEmpPage(p); loadEmployees(p); }}>Prev</Button><Button variant="outline" size="sm" disabled={empPage>=empPages} onClick={() => { const p=empPage+1; setEmpPage(p); loadEmployees(p); }}>Next</Button><Button variant="outline" size="sm" disabled={empPage>=empPages} onClick={() => { setEmpPage(empPages); loadEmployees(empPages); }}>Last</Button></div></div>)}
        </TabsContent>

        {/* Payroll */}
        <TabsContent value="payroll">
          <div className="flex items-center gap-3 mb-4">
            <SearchableSelect value={monthFilter} onChange={(v) => { setMonthFilter(v); setPayrollPage(1); }} placeholder="All Months" testid="" buttonClassName="w-44"
                  options={[{ value: 'all', label: 'All Months' }, ...payrollMonths.map(m => ({ value: m, label: m }))]} />
            {filters && <Select value={deptFilter} onValueChange={(v) => { setDeptFilter(v); setPayrollPage(1); }}><SelectTrigger className="w-44"><SelectValue placeholder="All Depts" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{filters.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>}
            <div className="relative flex-1 max-w-sm"><MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadPayroll(1)} className="pl-9" /></div>
          </div>
          {payrollTotals && (<div className="grid grid-cols-5 gap-4 mb-4">
            <div className="stats-card"><p className="text-xs text-[#64748B]">Salary Gross</p><p className="text-xl font-bold">{fmt(payrollTotals.total_salary_gross)}</p></div>
            <div className="stats-card"><p className="text-xs text-[#64748B]">Incentives</p><p className="text-xl font-bold text-blue-600">{fmt(payrollTotals.total_incentive)}</p></div>
            <div className="stats-card"><p className="text-xs text-[#64748B]">Net Pay</p><p className="text-xl font-bold text-green-600">{fmt(payrollTotals.total_net)}</p></div>
            <div className="stats-card"><p className="text-xs text-[#64748B]">PF</p><p className="text-xl font-bold text-amber-600">{fmt(payrollTotals.total_pf)}</p></div>
            <div className="stats-card"><p className="text-xs text-[#64748B]">Tax</p><p className="text-xl font-bold text-red-600">{fmt(payrollTotals.total_tax)}</p></div>
          </div>)}
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden"><table className="w-full text-sm"><thead className="bg-[#F8FAFC]"><tr><th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-left">Month</th><th className="px-3 py-2 text-left">Dept</th><th className="px-3 py-2 text-right">Basic</th><th className="px-3 py-2 text-right">Gross (Salary)</th><th className="px-3 py-2 text-right text-blue-700">Incentive</th><th className="px-3 py-2 text-right">PF</th><th className="px-3 py-2 text-right">Tax</th><th className="px-3 py-2 text-right">Net Pay</th><th className="px-3 py-2 text-center">Slip</th></tr></thead><tbody>{payroll.map((p, i) => (<tr key={i} className="border-t border-[#E2E8F0] cursor-pointer hover:bg-[#F8FAFC]" onClick={() => setSelectedEmployee(p.employee_number)}><td className="px-3 py-2"><p className="font-medium">{p.employee_name}</p><p className="text-xs text-[#64748B]">{p.employee_number}</p></td><td className="px-3 py-2">{p.payroll_month}</td><td className="px-3 py-2 text-sm">{p.department || '—'}</td><td className="px-3 py-2 text-right">{fmt(p.basic)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(p.salary_gross)}</td><td className="px-3 py-2 text-right font-medium text-blue-700">{p.performance_incentives > 0 ? fmt(p.performance_incentives) : '—'}</td><td className="px-3 py-2 text-right text-amber-600">{fmt(p.pf_employee)}</td><td className="px-3 py-2 text-right text-red-600">{fmt(p.income_tax)}</td><td className="px-3 py-2 text-right font-bold">{fmt(p.total_net_pay)}</td><td className="px-3 py-2 text-center"><Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); hrAPI.downloadSalarySlip(p.employee_number, p.payroll_month).catch(() => toast.error('Download failed')); }} className="h-7 w-7 p-0" data-testid={`payroll-slip-${i}`}><DownloadSimple size={14} className="text-[#0F4C81]" /></Button></td></tr>))}</tbody></table></div>
          {payrollPages > 1 && (<div className="flex items-center justify-between mt-3"><p className="text-sm text-[#64748B]">Page {payrollPage}/{payrollPages} ({payrollTotal})</p><div className="flex gap-1"><Button variant="outline" size="sm" disabled={payrollPage<=1} onClick={() => {setPayrollPage(1);loadPayroll(1);}}>First</Button><Button variant="outline" size="sm" disabled={payrollPage<=1} onClick={() => {const p=payrollPage-1;setPayrollPage(p);loadPayroll(p);}}>Prev</Button><Button variant="outline" size="sm" disabled={payrollPage>=payrollPages} onClick={() => {const p=payrollPage+1;setPayrollPage(p);loadPayroll(p);}}>Next</Button><Button variant="outline" size="sm" disabled={payrollPage>=payrollPages} onClick={() => {setPayrollPage(payrollPages);loadPayroll(payrollPages);}}>Last</Button></div></div>)}
        </TabsContent>

        <TabsContent value="leave"><LeaveManagement /></TabsContent>
        <TabsContent value="attendance"><AttendanceTab /></TabsContent>
        <TabsContent value="reimbursements"><ReimbursementTab /></TabsContent>
        <TabsContent value="assets"><AssetsTab /></TabsContent>
        <TabsContent value="regularizations"><RegularizationsTab /></TabsContent>
        <TabsContent value="config"><ConfigTab /></TabsContent>
      </Tabs>

      <EmployeeDetail employeeNumber={selectedEmployee} onClose={() => setSelectedEmployee(null)} />
    </div>
  );
}
